import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Autocomplete,
  IconButton,
  Chip,
} from '@mui/material';
import { AttachFile, Delete } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setStoreData, getAllAnnouncementClients, sendAnnouncement } from '../../Redux/actions';

export default function Announcements() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [errors, setErrors] = useState({});
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    // Check if there's pre-populated data from navigation state
    if (location.state?.prefilledBody) {
      setBody(location.state.prefilledBody);
    }
    if (location.state?.prefilledSubject) {
      setSubject(location.state.prefilledSubject);
    }
    
    // Select "All Clients" by default only when coming from UserManagement with pre-filled data
    if (clients.length > 0 && location.state?.prefilledBody && selectedClients.length === 0) {
      const allClientsOption = clients.find(c => c.id === 'all');
      if (allClientsOption) {
        setSelectedClients([allClientsOption]);
      }
    }
  }, [location.state, clients]);

  const fetchClients = async () => {
    dispatch(getAllAnnouncementClients()).then((resp) => {
      const response = resp?.data;
      if (response && response.success) {
        setClients(response.data);
      }
      setLoadingClients(false);
    }).catch(() => {
      setLoadingClients(false);
    });
  };

  const handleClientChange = (event, value) => {
    const err = Object.assign({}, errors);
    if (err['selectedClients']) {
      err['selectedClients'] = null;
    }
    setErrors(err);
    
    // If 'all' is selected, only keep 'all' in the selection
    const hasAllClients = value.some(client => client.id === 'all');
    if (hasAllClients) {
      setSelectedClients([clients.find(c => c.id === 'all')]);
    } else {
      setSelectedClients(value);
    }
  };

  const validateData = () => {
    const err = { ...errors };
    let hasErrors = false;

    if (!selectedClients || selectedClients.length === 0) {
      hasErrors = true;
      err.selectedClients = 'Please select at least one client';
    }

    if (!subject || subject.trim() === '') {
      hasErrors = true;
      err.subject = 'This field is mandatory';
    }

    if (!body || body.trim() === '') {
      hasErrors = true;
      err.body = 'This field is mandatory';
    }

    if (hasErrors) {
      setErrors(err);
      return false;
    }
    return true;
  };

  const handleChange = (fieldName, value) => {
    const err = Object.assign({}, errors);
    if (err[fieldName]) {
      err[fieldName] = null;
    }
    setErrors(err);
    
    if (fieldName === 'subject') {
      setSubject(value);
    } else if (fieldName === 'body') {
      setBody(value);
    }
  };

  const updateResponse = (resp, message) => {
    const res = resp && resp.data;
    let msg = res?.message || 'An error occurred';
    let type = 'fail';
    if (res && res.success) {
      msg = message;
      type = 'success';
    }
    dispatch(setStoreData('alertMessage', {
      msg,
      type,
      handleClose: () => handleCloseDialog(),
      handleDialogClose: () => handleDialogClose(),
    }));
  };

  const handleCloseDialog = () => {
    dispatch(setStoreData('alertMessage', null));
    // Reset form after successful send
    setSelectedClients([]);
    setSubject('');
    setBody('');
    setAttachments([]);
    setErrors({});
  };

  const handleDialogClose = () => {
    dispatch(setStoreData('alertMessage', null));
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      if (file.size > maxSize) {
        invalidFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      dispatch(setStoreData('alertMessage', {
        msg: `Files exceeding 10MB limit: ${invalidFiles.join(', ')}`,
        type: 'fail',
        handleClose: () => dispatch(setStoreData('alertMessage', null)),
        handleDialogClose: () => dispatch(setStoreData('alertMessage', null)),
      }));
    }

    setAttachments((prev) => [...prev, ...validFiles]);
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    setLoading(true);
    const checkForm = validateData();
    
    if (checkForm) {
      const clientIds = selectedClients.map(client => client.id);
      const formData = new FormData();
      
      const data = {
        client_ids: clientIds,
        subject: subject,
        body: body,
      };
      
      formData.append('data', JSON.stringify(data));
      
      // Append attachments
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      dispatch(sendAnnouncement(formData)).then((resp) => {
        setLoading(false);
        if (!resp) {
          return;
        } else {
          updateResponse(resp, resp?.data?.message);
        }
      });
    } else {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Clear all form fields
    setSelectedClients([]);
    setSubject('');
    setBody('');
    setAttachments([]);
    setErrors({});
    
    // Clear navigation state to prevent pre-filled values from repopulating
    navigate(location.pathname, { replace: true, state: {} });
  };


  if (loadingClients) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container justifyContent="center" style={{ padding: '24px' }}>
      <Grid item xs={12}>
        <Paper elevation={3} style={{ padding: '32px' }}>
          <Typography variant="h5" gutterBottom style={{ marginBottom: '24px' }}>
            Announcements
          </Typography>

          <Grid container spacing={3}>
            {/* To Field */}
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                multiple
                options={clients}
                value={selectedClients}
                filterSelectedOptions
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={handleClientChange}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                      sx={{
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderColor: '#008080',
                            },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#008080',
                        },
                    }}
                    {...params}
                    label="To"
                    placeholder="Select clients"
                    error={!!errors.selectedClients}
                    helperText={errors.selectedClients}
                    required
                  />
                )}
              />
            </Grid>

            {/* Subject Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                variant="outlined"
                value={subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder="Enter subject"
                error={!!errors.subject}
                helperText={errors.subject}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                          borderColor: '#008080',
                      },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                      color: '#008080',
                  },
              }}
              />
            </Grid>

            {/* Body Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Body"
                variant="outlined"
                multiline
                rows={12}
                value={body}
                onChange={(e) => handleChange('body', e.target.value)}
                placeholder="Enter your message"
                error={!!errors.body}
                helperText={errors.body}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                          borderColor: '#008080',
                      },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                      color: '#008080',
                  },
              }}
              />
            </Grid>
            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <input
                    accept="*/*"
                    style={{ display: 'none' }}
                    id="attachment-file"
                    multiple
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="attachment-file">
                    <Button
                      variant="outlined"
                      component="span"
                      size="large"
                      startIcon={<AttachFile />}
                      style={{ color: '#008080', borderColor: '#008080' }}
                    >
                      Attach Files
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" style={{ marginTop: '4px', color: '#666' }}>
                    Max: 10MB per file
                  </Typography>
                </Box>
                <Box display="flex" gap={2}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleCancel}
                    disabled={loading}
                    style={{ minWidth: '120px', color: '#008080', borderColor: '#008080' }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSend}
                    disabled={loading}
                    style={{ minWidth: '120px', backgroundColor: '#008080' }}
                  >
                    {loading ? <CircularProgress size={24} style={{ color: '#ffffff' }} /> : 'Send'}
                  </Button>
                </Box>
              </Box>
            </Grid>
            {/* Attachments Display */}
            {attachments.length > 0 && (
              <Grid item xs={12}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Attachments ({attachments.length}):
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {attachments.map((file, index) => (
                      <Chip
                        key={index}
                        label={`${file.name} (${(file.size / 1024).toFixed(2)} KB)`}
                        onDelete={() => handleRemoveAttachment(index)}
                        deleteIcon={<Delete />}
                        style={{ maxWidth: '300px' }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
